// Pattern: docs/design-patterns/frontend/component-patterns.md (layout container)
// Pattern: docs/design-patterns/frontend/accessibility.md
// Pattern: docs/design-patterns/frontend/responsive-design.md (44px touch targets, <lg hamburger)
//
// Slim (h-16) top bar: page chrome (title/subtitle/controls, via
// usePageChrome) on the left, ThemePicker + theme toggle + MenuDesktop
// (account dropdown, ALL breakpoints — B6.6, unlike the legacy Header,
// which hides it below lg) on the right, plus a <lg hamburger opening
// MobileNavDrawer. Reuses MenuDesktop verbatim ("reuse before build").
// Owns the drawer's open/closed state locally, per the FE-001 spec.
// OrgSwitcher moved to Sidebar (FE-003) — it no longer lives here.
import type { JSX } from 'react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';
import { Menu } from 'lucide-react';

import { usePageChrome } from '~/contexts/PageChromeContext';
import { useTheme } from '~/hooks/useTheme';

import { Logo } from '~/components/Logo';
import { MenuDesktop } from '~/components/MenuDesktop';

import { MobileNavDrawer } from './MobileNavDrawer';
import { findActiveNavItem, getNavSections } from './navConfig';
import { ThemePicker } from './ThemePicker';

export function TopBar(): JSX.Element {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { pathname } = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const activeItem = findActiveNavItem(getNavSections(), pathname);
  const chrome = usePageChrome(activeItem?.label);

  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-3 border-b border-default bg-surface-elevated px-4">
        <Button
          isIconOnly
          aria-label="Open navigation menu"
          aria-expanded={isDrawerOpen}
          variant="light"
          className="lg:hidden"
          onPress={() => setIsDrawerOpen(true)}
        >
          <Menu className="h-6 w-6" />
        </Button>

        <div className="lg:hidden">
          <Logo iconOnly />
        </div>

        <div className="min-w-0 flex-1">
          {chrome.breadcrumbs && chrome.breadcrumbs.length > 0 ? (
            <nav
              aria-label="Breadcrumb"
              className="flex items-center gap-1.5 truncate text-base text-muted"
            >
              {chrome.breadcrumbs.map((crumb, index) => (
                <span key={`${index}-${crumb.label}`} className="flex items-center gap-1.5">
                  {index > 0 && <span aria-hidden="true">&rsaquo;</span>}
                  {crumb.to ? (
                    <Link to={crumb.to} className="hover:text-foreground hover:underline">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span aria-current="page" className="font-medium text-foreground">
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          ) : (
            <h1 className="truncate text-base font-semibold text-foreground">{chrome.title}</h1>
          )}
          {chrome.subtitle && <p className="truncate text-xs text-muted">{chrome.subtitle}</p>}
        </div>

        {chrome.controls && (
          <div className="hidden items-center gap-2 sm:flex">{chrome.controls}</div>
        )}

        <ThemePicker />

        <Button aria-label="Toggle dark mode" isIconOnly onPress={toggleDarkMode} variant="light">
          {isDarkMode ? <SunIcon className="h-6 w-6" /> : <MoonIcon className="h-6 w-6" />}
        </Button>

        <MenuDesktop />
      </header>

      <MobileNavDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    </>
  );
}
